import { CanvasEdge, CanvasNode } from '../../../tabs/CampaignWorkflowTab/components/Canvas/Canvas';
import { CanvasState } from '../../../tabs/CampaignWorkflowTab/components/Canvas/state/CanvasState';
import { BranchableLeaf, Leaf, LinkedLeaf, Position } from '../types';
import { BranchNameNode } from '../Entities/Nodes/BranchNameNode';
import { BranchControlNode } from '../Entities/Nodes/BranchControlNode';
import { DisplayWebContentNode } from '../Entities/Nodes/DisplayWebContentNode';
import { DropzoneNode } from '../Entities/Nodes/DropzoneNode';
import { EndOfFlowNode } from '../Entities/Nodes/EndOfFlowNode';
import { SendEmailNode } from '../Entities/Nodes/SendEmailNode';
import { NodeTypeKeys } from './NodeTypeKeys';
import { TriggerNode } from '../Entities/Nodes/TriggerNode';

type CreatorConfig = { id: string; allowedNodes: string[]; position: Position; next?: Leaf; typeFor?: string };

export class StateToNodesMapper {
  public constructor(private state: CanvasState) {
    this.state = {
      ...state,
      nodes: state.nodes.map(s => s),
      edges: state.edges.map(s => s),
    };

    this.nodesDictionary = this.state.nodes.reduce((c, n) => {
      return {
        ...c,
        [n.id]: n,
      };
    }, {});
  }

  private nodesDictionary: { [k: string]: CanvasNode };

  private createNode(canvasNode: CanvasNode, edge: CanvasEdge, config?: Partial<CreatorConfig>) {
    const nodeIdsToCreator: { [key: string]: (config: CreatorConfig) => Leaf } = {
      [NodeTypeKeys.displayWebContentNodeType]: (c: CreatorConfig) =>
        new DisplayWebContentNode(c.id, c.allowedNodes, c.position, c.next),
      [NodeTypeKeys.sendEmailNodeType]: (c: CreatorConfig) =>
        new SendEmailNode(c.id, c.allowedNodes, c.position, c.next),
      [NodeTypeKeys.dropzoneNodeType]: (c: CreatorConfig) => new DropzoneNode(c.allowedNodes, c.position, c.next),
      [NodeTypeKeys.endOfFlowNodeType]: (c: CreatorConfig) => new EndOfFlowNode(c.id, c.position),
      [NodeTypeKeys.branchControlNodeType]: (c: CreatorConfig) => new BranchControlNode(c.id, c.typeFor, c.position),
      [NodeTypeKeys.branchNameNodeType]: (c: CreatorConfig) =>
        new BranchNameNode(c.id, c.typeFor, c.allowedNodes, c.position),
    };

    return nodeIdsToCreator[canvasNode.type]({
      id: canvasNode.id,
      allowedNodes: edge?.data.allowedNodes,
      typeFor: this.getBranchTypeFor(canvasNode),
      position: canvasNode.position,
      ...config,
    });
  }

  private getBranchTypeFor(controlNode: CanvasNode) {
    switch (controlNode.type) {
      /*
        Branch configuration nodes should receive the same type as their branch name nodes. 
        The configuration node uses this type to configure their branch name nodes when
        a new branch is being created.
      */
      case NodeTypeKeys.branchControlNodeType: {
        const node = this.nodesDictionary[controlNode.id];
        return node.data.restrictToType;
      }
      default: {
        const firstRootEdge = this.state.edges.filter(p => p.source === controlNode.id)[0];
        return this.nodesDictionary[firstRootEdge?.target]?.type;
      }
    }
  }

  public convert(): LinkedLeaf {
    const rootCanvasNode = this.state.nodes[0];
    const rootCanvasEdge = this.state.edges[0];

    const root = new TriggerNode(
      rootCanvasEdge?.data?.allowedNodes?.length ? rootCanvasEdge.data.allowedNodes : undefined,
      rootCanvasNode?.position,
    );

    this.state.nodes.splice(0, 1);

    this.state.nodes.forEach(node => {
      const prevId = this.state.edges.filter(s => s.target === node.id)[0]?.source;
      const source: Leaf = root.getNode(prevId);

      switch (node.type) {
        case NodeTypeKeys.branchControlNodeType: {
          const nodeTypeFor = this.getBranchTypeFor(node);
          const leaf = this.createNode(node, undefined, { typeFor: nodeTypeFor });

          source.addNode(leaf, prevId, false);

          break;
        }
        case NodeTypeKeys.branchNameNodeType: {
          const controlNode = source.getNode(prevId) as BranchableLeaf;

          const edge = this.state.edges.find(s => s.source === node.id);
          const leaf = this.createNode(node, edge);

          controlNode.createBranch(leaf);

          break;
        }
        default: {
          const edge = this.state.edges.find(p => p.source === node.id);
          const leaf = this.createNode(node, edge);

          source?.addNode(leaf, prevId, false);

          break;
        }
      }
    });

    return root;
  }
}
